import React from "react";
import {
  Palette,
  Layout,
  AppWindow,
  Smartphone,
  ArrowUpRight,
  Code2,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ITEMS = [
  {
    icon: Palette,
    title: "Graphic Design",
    desc: "Eye-catching logos, brand identities and marketing visuals that clearly communicate your message.",
    testid: "tech-graphic",
  },
  {
    icon: Layout,
    title: "Web Design",
    desc: "Seamless, intuitive user experiences and interfaces, with logical user flows from end to end.",
    testid: "tech-web-design",
  },
  {
    icon: AppWindow,
    title: "Web Applications",
    desc: "Custom, robust and scalable web applications built to expand your business reach.",
    testid: "tech-web-apps",
  },
  {
    icon: Smartphone,
    title: "Mobile Apps",
    desc: "Secure, scalable and intuitive mobile applications aligned with your business objectives.",
    testid: "tech-mobile",
  },
];

const SWARAM_STATS = [
  { kpi: "10+", label: "Years engineering" },
  { kpi: "50+", label: "Projects delivered" },
  { kpi: "4", label: "Product lines" },
];

export const Technology = () => {
  return (
    <section
      id="technology"
      data-testid="technology-section"
      className="relative bg-[#050505] py-16 md:py-24 lg:py-28 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_top,rgba(146,24,136,0.06),transparent_55%)]" />

      <div className="relative max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12 lg:px-16">
        {/* Heading */}
        <Reveal className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-fuchsia-500" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-fuchsia-400 font-bold">
              Technology · Design · Development
            </span>
            <span className="h-px w-10 bg-fuchsia-500" />
          </div>
          <h2
            data-testid="technology-headline"
            className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[64px] font-black tracking-[-0.03em] text-white leading-[0.98] max-w-5xl mx-auto"
          >
            Where ideas{" "}
            <span className="text-white/40 italic font-light">become</span>{" "}
            <span className="text-fuchsia-500">product.</span>
          </h2>
          <p className="mt-5 text-sm md:text-base text-white/55 max-w-3xl mx-auto leading-relaxed font-body">
            We craft high-performance, user-friendly digital products — from
            websites to mobile apps — partnering with businesses to bring their
            ideas to life and drive real results.
          </p>
        </Reveal>

        {/* Service grid */}
        <Reveal variant="scale" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-t border-l border-white/10">
          {ITEMS.map((it, i) => {
            const Icon = it.icon;
            return (
              <div
                key={it.title}
                data-testid={it.testid}
                className="group relative p-6 md:p-7 lg:py-10 border-r border-b border-white/10 hover:bg-fuchsia-500/[0.04] transition-colors duration-500"
              >
                <span className="font-display text-xs text-white/30 tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Icon className="w-6 h-6 text-fuchsia-400 mt-8 mb-5 group-hover:scale-110 transition-transform" />
                <h4 className="font-display text-lg md:text-xl font-bold text-white tracking-tight">
                  {it.title}
                </h4>
                <p className="mt-2 text-sm text-white/55 leading-relaxed">
                  {it.desc}
                </p>
                <div className="absolute bottom-0 left-0 h-px w-0 bg-fuchsia-500 group-hover:w-full transition-all duration-700" />
              </div>
            );
          })}
        </Reveal>

        {/* Sister company — Swaram Technologies */}
        <Reveal
          data-testid="swaram-partnership"
          className="mt-14 md:mt-20 relative overflow-hidden border border-white/10 bg-[#0a0a0c]"
        >
          {/* Decorative accent */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-fuchsia-500/[0.10] blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-fuchsia-700/[0.10] blur-3xl" />
          </div>

          <div className="relative p-8 md:p-12 lg:p-16">
            {/* Top: Eyebrow + headline (full width, centered) */}
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-6 border border-fuchsia-500/30 bg-fuchsia-500/[0.06] px-3 py-1.5">
                <Code2 className="w-3.5 h-3.5 text-fuchsia-400" />
                <span className="text-[10px] uppercase tracking-[0.28em] text-fuchsia-400 font-bold">
                  Sister Company · Engineering Partner
                </span>
              </div>
              <h3 className="font-display text-2xl md:text-3xl lg:text-[36px] xl:text-[42px] font-black tracking-[-0.03em] text-white leading-[1.1] max-w-3xl mx-auto" style={{ textWrap: "balance" }}>
                Engineering delivered by{" "}
                <span className="text-fuchsia-500 sm:whitespace-nowrap">
                  Swaram Technologies.
                </span>
              </h3>
              <p className="mt-5 text-sm md:text-base text-white/65 leading-relaxed max-w-2xl mx-auto font-body">
                For every web, mobile and platform build, we partner with our
                sister company{" "}
                <span className="text-white font-medium">
                  Swaram Technologies
                </span>{" "}
                — a Chennai-based product engineering studio that ships and
                operates software at scale.
              </p>
            </div>

            {/* Stats row — full width, 3 columns */}
            <div
              data-testid="swaram-stats"
              className="mt-10 md:mt-12 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl mx-auto"
            >
              {SWARAM_STATS.map((s) => (
                <div
                  key={s.label}
                  className="bg-[#0a0a0c] py-8 md:py-10 px-6 text-center hover:bg-fuchsia-500/[0.04] transition-colors duration-500"
                >
                  <div className="font-display text-5xl md:text-6xl font-black text-white tracking-tight">
                    {s.kpi}
                  </div>
                  <div className="mt-3 text-xs uppercase tracking-[0.22em] text-white/55 font-medium">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom: 2-col bullets + collab note */}
            <div className="mt-12 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <div className="lg:col-span-7">
                <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 font-bold mb-4">
                  What they bring
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm md:text-[15px] text-white/75">
                  {[
                    "Product-minded engineering partner",
                    "Senior architects from kickoff to launch",
                    "Web, SaaS, CRM/ERP & native mobile",
                    "Security, observability & docs included",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-fuchsia-500 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
                  <a
                    href="https://swaramtech.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="swaram-cta-visit"
                    className="group inline-flex w-full sm:w-auto items-center justify-center gap-2.5 bg-fuchsia-500 hover:bg-fuchsia-400 text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-all duration-300 ember-glow"
                  >
                    Visit Swaram Technologies
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                  </a>
                  <a
                    href="https://swaramtech.com/#services"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="swaram-cta-services"
                    className="inline-flex w-full sm:w-auto items-center justify-center gap-2 border border-white/20 hover:border-fuchsia-400 hover:bg-white/5 text-white text-sm font-semibold px-6 py-3.5 rounded-full transition-all"
                  >
                    Their full stack
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="border border-white/10 bg-white/[0.02] p-6 md:p-7">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-fuchsia-400 font-bold mb-3">
                    How we collaborate
                  </div>
                  <p className="text-sm md:text-[15px] text-white/70 leading-relaxed">
                    1902 handles strategy, creative, partnerships and growth.
                    Swaram engineers the product behind it — one combined team,
                    one accountable delivery.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
